import { useRef, useState } from "react";
import { Lock } from "lucide-react";

/**
 * 3-second hold-to-release lockdown pill.
 *
 * Self-contained:
 *  - Owns its own progress state + rAF ref so the parent Board doesn't
 *    have to wire timers through props.
 *  - Calls `onRelease()` exactly once when the 3s hold completes.
 *  - Cancels cleanly on pointer-up / pointer-leave.
 *
 * Extracted from Board.jsx as a pure refactor. The visual + behavior is
 * byte-identical to the inline button it replaces — same testid, same
 * progress bar, same labels.
 */
export default function LockdownReleaseButton({ onRelease }) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  const stop = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setProgress(0);
  };

  const startHold = () => {
    const start = Date.now();
    const tick = () => {
      const pct = Math.min(1, (Date.now() - start) / 3000);
      setProgress(pct);
      if (pct < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        setProgress(0);
        onRelease?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  return (
    <button
      type="button"
      className="pill-btn relative overflow-hidden"
      aria-label="Hold to exit lockdown"
      data-testid="board-lockdown-release"
      onPointerDown={startHold}
      onPointerUp={stop}
      onPointerLeave={stop}
    >
      <Lock size={16} />
      <span className="hidden sm:inline">Hold to exit</span>
      {progress > 0 && (
        <span
          aria-hidden
          className="absolute left-0 bottom-0 h-1 bg-[color:var(--coral)]"
          style={{ width: `${progress * 100}%` }}
        />
      )}
    </button>
  );
}
